import menuLayanan from '@/shared/jsons/menu-layanan.json'
import ExportedImage from 'next-image-export-optimizer'
import Link from 'next/link'
import { MdOutlineLocationOn, MdOutlineMail, MdOutlinePhone } from 'react-icons/md'
import { RiFacebookCircleFill, RiTwitterXFill } from 'react-icons/ri'
import { RxInstagramLogo, RxLinkedinLogo } from 'react-icons/rx'

import Container from '@/components/templates/container/container'

interface FooterProps {
  language?: 'id' | 'en'
}

export const Footer = ({ language = 'id' }: FooterProps) => {
  const menuLayananActive = language === 'en' ? menuLayanan.en : menuLayanan.id

  return (
    <footer className="w-full">
      <div className="py-12 lg:py-16 bg-secondary-blue">
        <Container>
          <div className="flex flex-col justify-between gap-11 lg:gap-20 lg:flex-row">
            <div className="lg:w-6/12">
              <div className="flex flex-col gap-4 lg:gap-6 lg:flex-row">
                <Link href="/" className="relative">
                  <ExportedImage
                    src="/easybiz-hoc-white.png"
                    alt="Easybiz logo"
                    width={120}
                    height={85}
                    sizes="120px"
                    unoptimized
                    className="object-contain"
                  />
                </Link>

                <p className="text-sm font-semibold text-gray-200 lg:w-10/12">
                  <span className="text-secondary-yellow">Easybiz </span>
                  {language === 'en'
                    ? 'has been trusted to assist thousands of corporate reporting obligations and business licenses for micro, small, medium, and large scale of various business models.'
                    : 'telah dipercaya membantu ribuan Pendirian Perusahaan dan Pengurusan Perizinan Berusaha dari skala mikro, kecil, menengah, besar dan dengan beragam model bisnis.'}
                </p>
              </div>

              <div className="w-full h-[1px] bg-gray-400 my-8" />

              <div className="space-y-5">
                <h2 className="font-bold text-white uppercase">
                  {language === 'en' ? 'ADDRESS & CONTACT' : 'Alamat & Kontak'}
                </h2>
                <ul className="space-y-3 text-sm font-medium text-gray-200">
                  <li className="hover:text-white">
                    <Link
                      target="_blank"
                      className="flex space-x-2"
                      href="https://www.google.com/maps/place/Justika.com/@-6.2963892,106.8232827,17z/data=!4m14!1m7!3m6!1s0x2e69f354e2758ec5:0xe0e98e20df3c4b67!2sJustika.com!8m2!3d-6.2963892!4d106.8258576!16s%2Fg%2F11fl4l087g!3m5!1s0x2e69f354e2758ec5:0xe0e98e20df3c4b67!8m2!3d-6.2963892!4d106.8258576!16s%2Fg%2F11fl4l087g?entry=ttu"
                    >
                      <MdOutlineLocationOn size={20} className="w-5 h-5" />
                      <p className="w-11/12 max-w-[400px]">
                        PT Justika Solusi Indonesia, AD Premier Office Park Lantai 9 Jl. TB Simatupang No. 5 Ragunan, Pasar
                        Minggu Jakarta
                      </p>
                    </Link>
                  </li>
                  <li className="hover:text-white">
                    <Link target="_blank" href="tel:02122708903" className="flex items-center space-x-2 gtm-contact-phone">
                      <MdOutlinePhone size={20} className="w-5 h-5" />
                      <p className="w-11/12">02122708903</p>
                    </Link>
                  </li>
                  <li className="hover:text-white">
                    <Link
                      target="_blank"
                      href="mailto:halo@easybiz.id"
                      className="flex items-center space-x-2 gtm-contact-email"
                    >
                      <MdOutlineMail size={20} className="w-5 h-5" />
                      <p className="w-11/12">halo@easybiz.id</p>
                    </Link>
                  </li>
                </ul>
              </div>
            </div>

            <div className="flex lg:justify-end lg:w-4/12">
              <div className="space-y-5">
                <h2 className="font-bold text-white uppercase">
                  {language === 'en' ? 'EASYBIZ PRODUCTS' : 'Layanan Easybiz'}
                </h2>
                <ul className="space-y-3 text-sm font-medium text-gray-200">
                  {menuLayananActive.map((menu, index) => (
                    <li key={index} className="hover:text-white">
                      <Link href={menu.url}>{menu.label}</Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="flex lg:justify-end lg:w-3/12">
              <div className="space-y-5">
                <h2 className="font-bold text-white uppercase">
                  {language === 'en' ? 'HELP & GUIDE' : 'Bantuan & Panduan'}
                </h2>
                <ul className="space-y-3 text-sm font-medium text-gray-200">
                  <li className="hover:text-white">
                    <Link href="/tentang-kami">{language === 'en' ? 'About Us' : 'Tentang Kami'}</Link>
                  </li>
                  <li className="hover:text-white">
                    <Link href="/syarat-penggunaan-layanan">
                      {language === 'en' ? 'Terms of Service ' : 'Syarat Penggunaan Layanan'}
                    </Link>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </Container>
      </div>

      <div className="bg-[#021223] w-full py-7 text-white">
        <Container className="flex flex-col justify-center gap-5 text-xs text-center lg:flex-row lg:text-start lg:items-center lg:justify-between">
          <p>
            &copy; {new Date().getFullYear()}{' '}
            {language === 'en' ? 'Easybiz. All rights reserved.' : 'Easybiz. Semua hak dilindungi undang-undang.'}
          </p>
          <ul className="flex items-center justify-center gap-x-3 lg:justify-normal">
            <li>
              <Link href="https://www.facebook.com/easybizID" target="_blank" aria-label="Facebook">
                <RiFacebookCircleFill size={28} />
              </Link>
            </li>
            <li>
              <Link href="https://www.instagram.com/easybiz.id" target="_blank" aria-label="Instagram">
                <RxInstagramLogo size={24} />
              </Link>
            </li>
            <li>
              <Link href="https://twitter.com/easybizid" target="_blank" aria-label="Twitter">
                <RiTwitterXFill size={24} />
              </Link>
            </li>
            <li>
              <Link href="https://www.linkedin.com/company/easybiz" target="_blank" aria-label="LinkedIn">
                <RxLinkedinLogo size={24} />
              </Link>
            </li>
          </ul>
        </Container>
      </div>
    </footer>
  )
}
